import copy from 'copy-to-clipboard';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';

import {
  changeUserSpaceMembership,
  SpaceMembershipOperation,
} from '../../../api/Rest/space';
import {
  AvatarNode,
  Card,
  CountDownText,
  FallbackActionGroup,
  InvitationStepsButtonGroup,
  SpaceCard,
  SpaceCardProps,
  TextButton,
  TextButtonSize,
  TextButtonVariant,
  TextInput,
  UserViewCard,
} from '../../../components/Molecules';

import { DEFAULT_APP_STORE_URL } from '../../../constants/appInfo';
import { NOT_APPLICABLE_STRING } from '../../../constants/general';
import { Icons, IconType } from '../../../constants/icons';
import { getInvitationMessage } from '../../../constants/shareInvitationLink';
import { SpaceType } from '../../../constants/space';
import { emailSchema } from '../../../constants/validationSchemas';
import useDeepLink from '../../../hooks/useDeeplink';
import { ProfileMapType } from '../../../models/UserStore';
import {
  InvitationLink,
  UserInvitationType,
} from '../../../models/UserStore/Invitation';
import { useSpaceStore } from '../../../stores/SpaceStore';
import { useUserStore } from '../../../stores/UserStore';
import { getDefaultBannerSource } from '../../../utils/banner';
import { getFromI18n } from '../../../utils/i18n';

type ClassroomSharingScreenProps = {
  invitationLink: InvitationLink;
};

export const ClassroomSharingScreen = (props: ClassroomSharingScreenProps) => {
  const { invitationLink } = props;
  const [currentStep, setCurrentStep] = useState<
    'info' | 'register' | 'result'
  >('info');
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(
    'loaded',
  );
  const { t } = useTranslation();

  const contentInviterUserId = invitationLink.userId;
  const contentInviterProfile = useUserStore
    .getState()
    .getProfile(contentInviterUserId);

  //album
  const classroomId =
    UserInvitationType.ClassroomSharing == invitationLink?.type &&
    'classroomId' in invitationLink.content
      ? invitationLink.content.classroomId
      : NOT_APPLICABLE_STRING;

  const classroom = useSpaceStore
    .getState()
    .getSpace(classroomId ?? NOT_APPLICABLE_STRING);

  const spaceCardData: SpaceCardProps = useMemo(() => {
    const classroomInfo = getFromI18n(classroom?.i18nClassroomInfo);
    return {
      banner:
        classroomInfo?.carousel[0]?.image?.publicUrls?.medium ??
        getDefaultBannerSource(classroom?.classroomId) ??
        '',
      icon: classroom?.icon?.publicUrls?.small ?? '',
      classroomName: classroomInfo?.name ?? '',
      followerCount: classroom?.statistics?.followerCount ?? 0,
      postCount: classroom?.statistics?.classroomChannelPublicPostCount ?? 0,
      playCount: classroom?.statistics?.playCount ?? 0,
      memberTotalCount: classroom?.memberTotal ?? 0,
    };
  }, [
    classroom?.classroomId,
    classroom?.i18nClassroomInfo,
    classroom?.icon?.publicUrls?.small,
    classroom?.memberTotal,
    classroom?.statistics?.classroomChannelPublicPostCount,
    classroom?.statistics?.followerCount,
    classroom?.statistics?.playCount,
  ]);

  const classroomOwnerProfile = useUserStore
    .getState()
    .getProfileFromMap(
      ProfileMapType.SpaceRoot,
      classroom?.classroomId ?? NOT_APPLICABLE_STRING,
    );

  const expiryAt = invitationLink.expiredAt;

  //

  const [form, setForm] = useState<{
    email: string;
    password?: string;
    agreeStatement: boolean;
    emailErrorMessage?: string;
  }>({
    email: invitationLink?.email ?? '',
    password: undefined,
    agreeStatement: false,
  });
  const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const validateResult = emailSchema.safeParse(event.target.value);

    setForm((s) => ({
      ...s,
      email: event.target.value,
      emailErrorMessage: validateResult.success
        ? undefined
        : 'Please enter a valid email address.',
    }));
  }, []);

  const submit = useCallback(async () => {
    try {
      setStatus('loading');
      const response = await changeUserSpaceMembership(
        { classroomId: classroomId },
        {
          operation: SpaceMembershipOperation.Join,
          email: form.email,
          userCode: invitationLink?.userCode,
        },
      );

      if (response.ok) {
        const password = response.data.password;
        if (password) {
          setForm((s) => ({ ...s, password: password }));
        }
        setStatus('loaded');
        setCurrentStep('result');
      } else {
        toast.error(t('There is something wrong.'));
        setStatus('error');
      }
    } catch (error) {
      console.error(error);
    }
  }, [classroomId, form.email, invitationLink?.userCode, t]);
  const onClick = () => {
    submit().catch((error) => {
      console.error(error);
    });
  };

  const onPressCopy = () => {
    copy(form.password ?? '');
    toast.success(t('Password is copied.'));
  };

  const onPresOpenAppElseAppStore = useDeepLink(
    `joyajoyapp://user-invite?invitationLinkId=${invitationLink.invitationLinkId}`,
  );

  if (status == 'loading')
    return (
      <>
        <div
          className="flex flex-col justify-center items-center"
          style={{ flex: 1 }}
        >
          <Spinner
            animation="border"
            role="status"
            style={{ color: '#05656B' }}
          >
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </>
    );

  if (currentStep == 'info') {
    return (
      <>
        <div
          className="flex flex-col share-content"
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem',
          }}
        >
          <Card style={{ width: '100%', maxWidth: '480px' }}>
            {contentInviterProfile ? (
              <>
                <AvatarNode
                  avatar={{
                    ...contentInviterProfile.avatar,
                    userId: contentInviterProfile.userId,
                    source:
                      contentInviterProfile.profilePicture &&
                      contentInviterProfile.profilePicture.publicUrls?.small
                        ? {
                            uri: contentInviterProfile.profilePicture.publicUrls
                              .small,
                          }
                        : NOT_APPLICABLE_STRING,
                  }}
                  size={80}
                />
                <p
                  className="text-lg text-cyan-800"
                  style={{ textAlign: 'center', padding: '1rem' }}
                >
                  <span className="font-bold">
                    {contentInviterProfile.name}{' '}
                  </span>
                  {t(
                    getInvitationMessage(
                      classroom?.type == SpaceType.Home
                        ? UserInvitationType.UserSharing
                        : UserInvitationType.ClassroomSharing,
                    ),
                  )}
                </p>
              </>
            ) : null}
            {classroom?.type == SpaceType.Home ? (
              <UserViewCard profile={classroomOwnerProfile} />
            ) : classroom ? (
              <SpaceCard
                banner={spaceCardData.banner}
                icon={spaceCardData.icon}
                classroomName={spaceCardData.classroomName}
                postCount={spaceCardData.postCount}
                followerCount={spaceCardData.followerCount}
                memberTotalCount={spaceCardData.memberTotalCount}
                playCount={spaceCardData.playCount}
                //

                style={{ backgroundColor: '#f2f2f2' }}
              />
            ) : null}

            <div
              className="flex flex-col items-center w-full"
              style={{ padding: '0.25rem', marginTop: '16px' }}
            >
              {/* <p className="text-lg font-bold">
                {t('Click OK to view details')}
              </p>
              <TextButton
                text={t('OK')}
                size={TextButtonSize.Large}
                variant={TextButtonVariant.Cyan}
                onClick={onPresOpenAppElseAppStore}
              /> */}
              <InvitationStepsButtonGroup
                onPressAccept={onPresOpenAppElseAppStore}
              />
              <CountDownText
                timestamp={expiryAt.getTime()}
                label={t('Expiry: ')}
                className="text-sm font-bold text-red-550"
              />
              <FallbackActionGroup />
            </div>
          </Card>
        </div>
      </>
    );
  }

  if (currentStep == 'register') {
    return (
      <div
        className="flex flex-col"
        style={{ flex: 1, backgroundColor: '#EDEDED' }}
      >
        <section
          className="flex flex-col items-center justify-center p-3"
          style={{ gap: '1rem' }}
        >
          <img
            className="classroom-icon size-icon-2xl"
            src={
              Icons[
                classroom?.type == SpaceType.Home
                  ? IconType.ParticipantLine
                  : IconType.ClassroomLine
              ]
            }
          />
          <p className="text-2xl font-bold">
            {t(
              classroom?.type == SpaceType.Home
                ? 'Explore & Follow User'
                : 'Explore & Follow Club',
            )}
          </p>

          {classroom?.type == SpaceType.Home ? (
            <UserViewCard
              profile={classroomOwnerProfile}
              backgroundColor={'#ffffff'}
            />
          ) : classroom ? (
            <SpaceCard
              banner={spaceCardData.banner}
              icon={spaceCardData.icon}
              classroomName={spaceCardData.classroomName}
              postCount={spaceCardData.postCount}
              followerCount={spaceCardData.followerCount}
              memberTotalCount={spaceCardData.memberTotalCount}
              playCount={spaceCardData.playCount}
              //

              style={{ backgroundColor: '#ffffff' }}
            />
          ) : null}

          <hr />
          <p
            style={{
              textAlign: 'center',
              padding: '0rem 1.5rem',
              maxWidth: '600px',
            }}
          >
            {t(
              'If there is no JOYAJOY account with the following email, a new account will be created for you.',
            )}
          </p>
          <TextInput
            value={form.email}
            placeholder={t('Email')}
            onChange={onEmailChange}
            errorMessage={form.emailErrorMessage}
          />
          <div className="flex flex-row gap-2 items-center">
            <input
              type="checkbox"
              id="accept-policy"
              checked={form.agreeStatement}
              onChange={() =>
                setForm((s) => ({ ...s, agreeStatement: !s.agreeStatement }))
              }
            />
            <div>
              <label htmlFor="accept-policy">
                <Trans i18nKey="Accept Terms">
                  I agree to JOYAJOY&apos;s
                  <a href="https://joyajoy.com/zh/pages/term-of-services">
                    Terms and Conditions
                  </a>
                  and
                  <a href="https://joyajoy.com/zh/pages/privacy-policy">
                    Privacy Statement
                  </a>
                </Trans>
              </label>
            </div>
          </div>

          <hr />
          <p className="text-xl">
            {t(
              classroom?.type == SpaceType.Home
                ? 'Explore & Follow now!'
                : 'Explore & Follow Club now!',
            )}
          </p>
          <TextButton
            text={t('Explore')}
            size={TextButtonSize.Large}
            variant={TextButtonVariant.Cyan}
            onClick={onClick}
            disabled={
              form.agreeStatement == false ||
              form.emailErrorMessage != undefined
            }
          />
          <CountDownText
            timestamp={expiryAt.getTime()}
            label={t('Expiry: ')}
            className="text-sm font-bold text-red-550"
          />
        </section>
      </div>
    );
  }

  if (currentStep == 'result') {
    return (
      <div
        className="flex flex-col"
        style={{ flex: 1, backgroundColor: '#EDEDED' }}
      >
        <section
          className="flex flex-col items-center justify-center p-3"
          style={{ gap: '1rem' }}
        >
          <img
            className="classroom-icon size-icon-2xl"
            src={Icons[IconType.Tick]}
          />
          <p className="text-2xl font-bold">
            {t(
              classroom?.type == SpaceType.Home
                ? 'Invitation to Follow User'
                : 'Invitation to Club',
            )}
          </p>

          {classroom?.type == SpaceType.Home ? (
            <UserViewCard
              profile={classroomOwnerProfile}
              backgroundColor={'#ffffff'}
            />
          ) : classroom ? (
            <SpaceCard
              banner={spaceCardData.banner}
              icon={spaceCardData.icon}
              classroomName={spaceCardData.classroomName}
              postCount={spaceCardData.postCount}
              followerCount={spaceCardData.followerCount}
              memberTotalCount={spaceCardData.memberTotalCount}
              playCount={spaceCardData.playCount}
              //

              style={{ backgroundColor: '#f2f2f2' }}
            />
          ) : null}
          <p style={{ textAlign: 'center', maxWidth: '600px' }}>
            {t(
              classroom?.type == SpaceType.Home
                ? 'You are invited to explore and follow the user. A JOYAJOY account has been created for you!'
                : 'You are invited to explore and follow the Club. A JOYAJOY account has been created for you!',
            )}
          </p>
          <div
            className="rounded-lg"
            style={{
              overflow: 'auto',
              background: 'white',
              padding: '0.75rem 1.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
            }}
          >
            <div className="flex flex-row items-center gap-1">
              <img className="size-icon-m" src={Icons[IconType.Email]} />
              <p>{t('Email: {email}', { email: form.email })}</p>
            </div>
            {form.password ? (
              <div className="flex flex-row items-center gap-1">
                <img className="size-icon-m" src={Icons[IconType.Password]} />
                <p>{t('Password: {password}', { password: form.password })}</p>
                <img
                  onClick={onPressCopy}
                  className="size-icon-s icon-button"
                  src={Icons[IconType.Copy]}
                />
              </div>
            ) : null}
          </div>
          <TextButton
            text={t('OK')}
            size={TextButtonSize.Small}
            variant={TextButtonVariant.Cyan}
            onClick={() => {
              window.open(
                DEFAULT_APP_STORE_URL,
                '_blank',
                'noopener,noreferrer',
              );
            }}
          />
        </section>
      </div>
    );
  }
  return <></>;
};
